/**
 * Resource Enhanced Component
 * Unique Properties: Allocation dashboard, capacity planning, utilization, reservations
 */

import { Alert, Badge, Card, Container, Grid, Group, Paper, Progress, Stack, Tabs, Text, Title } from '@mantine/core';
import { Calendar } from '@mantine/dates';
import { IconCalendar, IconChartBar, IconDatabase } from '@tabler/icons-react';
import React, { useEffect, useRef, useState } from 'react';
import { TabulatorFull } from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator_midnight.min.css';
import WorkflowSearchMenu from './WorkflowSearchMenu';

interface ResourceProps {
    data?: any;
    mode?: 'allocate' | 'monitor' | 'plan';
}

export const ResourceEnhanced: React.FC<ResourceProps> = ({ data = {}, mode = 'monitor' }) => {
    const [activeTab, setActiveTab] = useState('allocation');
    const tableRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (tableRef.current) {
            new TabulatorFull(tableRef.current, {
                data: [],
                layout: 'fitColumns',
                columns: [
                    { title: 'Resource', field: 'name', headerFilter: true },
                    { title: 'Type', field: 'type' },
                    { title: 'Capacity', field: 'capacity' },
                    { title: 'Used', field: 'used' },
                    {
                        title: 'Available', field: 'available', formatter: (cell: any) => {
                            const value = cell.getValue();
                            const color = value > 50 ? 'green' : value > 20 ? 'orange' : 'red';
                            return `<span style="color: ${color};">${value}%</span>`;
                        }
                    }
                ]
            });
        }
    }, []);

    return (
        <Container size="xl">
            <Paper shadow="sm" p="lg">
                <Title order={2} mb="lg">Resource Management</Title>
                <Tabs value={activeTab} onChange={(v) => setActiveTab(v ?? '')}>
                    <Tabs.List>
                        <Tabs.Tab value="allocation" leftSection={<IconDatabase size={16} />}>Allocation</Tabs.Tab>
                        <Tabs.Tab value="utilization" leftSection={<IconChartBar size={16} />}>Utilization</Tabs.Tab>
                        <Tabs.Tab value="calendar" leftSection={<IconCalendar size={16} />}>Reservations</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel value="allocation">
                        <Stack mt="md">
                            <Grid>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Text size="sm" color="dimmed">CPU Cores</Text>
                                        <Group justify="space-between" mt="xs">
                                            <Title order={3}>16 / 32</Title>
                                            <Badge color="green">50% Free</Badge>
                                        </Group>
                                        <Progress value={50} mt="xs" />
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Text size="sm" color="dimmed">Memory</Text>
                                        <Group justify="space-between" mt="xs">
                                            <Title order={3}>24 / 64 GB</Title>
                                            <Badge color="orange">37.5% Free</Badge>
                                        </Group>
                                        <Progress value={62.5} color="orange" mt="xs" />
                                    </Card>
                                </Grid.Col>
                                <Grid.Col span={4}>
                                    <Card withBorder>
                                        <Text size="sm" color="dimmed">Storage</Text>
                                        <Group justify="space-between" mt="xs">
                                            <Title order={3}>500 / 1000 GB</Title>
                                            <Badge color="green">50% Free</Badge>
                                        </Group>
                                        <Progress value={50} mt="xs" />
                                    </Card>
                                </Grid.Col>
                            </Grid>
                            <div ref={tableRef} />
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="utilization">
                        <Stack mt="md">
                            <Alert color="blue">
                                Resource utilization over the last 24 hours
                            </Alert>
                            <Card withBorder>
                                <Title order={5} mb="md">Utilization Trends</Title>
                                <Stack gap="xs">
                                    <div>
                                        <Group justify="space-between" mb="xs">
                                            <Text size="sm">Peak Usage</Text>
                                            <Badge color="red">92%</Badge>
                                        </Group>
                                        <Text size="xs" color="dimmed">Today at 2:30 PM</Text>
                                    </div>
                                    <div>
                                        <Group justify="space-between" mb="xs">
                                            <Text size="sm">Average Usage</Text>
                                            <Badge color="orange">65%</Badge>
                                        </Group>
                                        <Text size="xs" color="dimmed">Last 24 hours</Text>
                                    </div>
                                    <div>
                                        <Group justify="space-between" mb="xs">
                                            <Text size="sm">Minimum Usage</Text>
                                            <Badge color="green">12%</Badge>
                                        </Group>
                                        <Text size="xs" color="dimmed">Today at 3:00 AM</Text>
                                    </div>
                                </Stack>
                            </Card>
                        </Stack>
                    </Tabs.Panel>
                    <Tabs.Panel value="calendar">
                        <Stack mt="md">
                            <Calendar />
                        </Stack>
                    </Tabs.Panel>
                </Tabs>
            </Paper>
        </Container>
    );
};

export default ResourceEnhanced;
